import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/axios';

const PublicStandings = () => {
    const { id } = useParams();
    const [tournament, setTournament] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchPublicTournament = async () => {
            try {
                // Assuming you have a public route, or your standard route allows read-only access
                const { data } = await api.get(`/tournaments/${id}`);
                setTournament(data);
            } catch (err) {
                console.error("Failed to load public standings", err);
                setError("Tournament not found or is private.");
            } finally {
                setLoading(false);
            }
        };
        fetchPublicTournament();
    }, [id]);

    if (loading) return (
        <div className="flex justify-center items-center min-h-screen bg-gray-50">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
    );

    if (error || !tournament) return (
        <div className="flex justify-center items-center min-h-screen bg-gray-50">
            <div className="text-xl font-black text-gray-500">{error || "Tournament not found."}</div>
        </div>
    );

    const gameType = tournament.settings?.gameType || 'Catan';
    const isCatan = gameType === 'Catan' || gameType === 'Catan Seafarers';
    const format = tournament.settings?.format || 'Swiss';
    const maxRounds = tournament.settings?.maxRounds || 3;
    const currentRound = tournament.currentRound || 1;
    const tieOrder = tournament.settings?.tieBreakerOrder || ['totalWinPts', 'totalPercentage', 'totalGamePts'];

    const sortedPlayers = [...tournament.players].sort((a, b) => {
        for (const criterion of tieOrder) {
            if (b[criterion] !== a[criterion]) {
                return (b[criterion] || 0) - (a[criterion] || 0);
            }
        }
        return 0;
    });

    return (
        <div className="min-h-screen bg-gray-50 pb-20 font-sans">
            {/* PUBLIC HEADER */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
                    <div>
                        <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                            <span className="bg-blue-100 text-blue-700 font-black px-2 py-0.5 rounded text-[10px] uppercase tracking-widest">{gameType}</span>
                            <span className="bg-gray-100 text-gray-600 font-bold px-2 py-0.5 rounded text-[10px] uppercase tracking-widest">LIVE STANDINGS</span>
                        </div>
                        <h1 className="text-3xl font-black text-gray-900">{tournament.name}</h1>
                        <p className="text-sm font-bold text-gray-500 uppercase tracking-widest mt-1">
                            Round {currentRound} / {maxRounds} • {tournament.status}
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 mt-8 overflow-x-auto pb-8">
                {format === 'Knockout' ? (
                    /* --- PUBLIC LIGHT THEME KNOCKOUT BRACKET --- */
                    <div className="flex gap-12 min-w-max items-center justify-start py-10 px-6 bg-white rounded-3xl border border-gray-200 shadow-sm overflow-x-auto">
                        {tournament.rounds.map((round) => (
                            <div key={round.roundNumber} className="flex flex-col justify-around gap-8 w-64 min-h-[400px]">
                                <div className="text-center bg-gray-50 py-3 rounded-xl border border-gray-100">
                                    <h3 className="text-sm font-black text-blue-600 uppercase tracking-widest">
                                        {round.roundNumber === maxRounds ? '🏆 Finals' : `Round ${round.roundNumber}`}
                                    </h3>
                                </div>
                                <div className="flex flex-col justify-around h-full gap-6">
                                    {round.groups.map((match, mIdx) => (
                                        <div key={mIdx} className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col border border-gray-100">
                                            <div className="bg-gray-50 px-3 py-2 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-100 flex justify-between items-center">
                                                <span>Match {mIdx + 1}</span>
                                                {match.players.length === 1 && <span className="text-blue-600 bg-blue-100 px-2 py-0.5 rounded uppercase">Bye</span>}
                                            </div>
                                            <div className="divide-y divide-gray-50">
                                                {match.players.map((p, i) => (
                                                    <div key={p.playerId || i} className={`px-4 py-3 flex justify-between items-center ${p.winPts > 0 ? 'bg-blue-50/50' : ''}`}>
                                                        <span className={`truncate w-36 text-sm ${p.winPts > 0 ? 'font-black text-blue-700' : 'font-bold text-gray-600'}`}>
                                                            {p.name}
                                                        </span>
                                                        <span className={`font-black w-8 h-8 flex items-center justify-center rounded-lg text-sm ${
                                                            p.winPts > 0 
                                                            ? 'bg-blue-600 text-white shadow-sm' 
                                                            : p.gamePts !== null ? 'bg-gray-100 text-gray-600' : 'bg-gray-50 text-gray-300 border border-gray-100'
                                                        }`}>
                                                            {p.gamePts !== null ? p.gamePts : '-'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    /* --- PUBLIC SWISS / ROUND ROBIN TABLE --- */
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200 text-[10px] font-black uppercase tracking-widest text-gray-400">
                                    <th className="px-6 py-4 text-center">Rank</th>
                                    <th className="px-6 py-4">Player Name</th>
                                    <th className="px-6 py-4 text-center">{isCatan ? 'Tournament Pts' : 'Total Points'}</th>
                                    {isCatan && (
                                        <>
                                            <th className="px-6 py-4 text-center">Win %</th>
                                            <th className="px-6 py-4 text-center">Game Pts</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sortedPlayers.map((player, index) => (
                                    <tr key={player.id} className="hover:bg-blue-50/30 transition-colors">
                                        <td className="px-6 py-4 text-center">
                                            {index < 3 ? (
                                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-black text-sm ${
                                                    index === 0 ? 'bg-yellow-400 text-yellow-900' : 
                                                    index === 1 ? 'bg-gray-300 text-gray-700' : 'bg-orange-400 text-orange-900'
                                                }`}>
                                                    {index + 1}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 font-bold">{index + 1}</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 font-bold text-gray-800">{player.name}</td>
                                        <td className="px-6 py-4 text-center font-black text-gray-700">{player.totalWinPts || 0}</td>
                                        {isCatan && (
                                            <>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-black">
                                                        {(player.totalPercentage || player.percentage || 0).toFixed(2)}%
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-center font-bold text-gray-400">{player.totalGamePts || 0}</td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PublicStandings;



 